export interface AgentStreamRequest {
  endpoint: string;
  prompt: string;
  sessionId: string;
  signal: AbortSignal;
  onEvent: (event: AgentStreamEvent) => void;
}

export type AgentStreamEvent =
  | { type: 'text'; content: string }
  | { type: 'thinking'; content: string }
  | { type: 'error'; message: string }
  | { type: 'tool-start'; toolUseId: string; name: string }
  | { type: 'tool-input'; content: string }
  | { type: 'tool-stop' }
  | { type: 'tool-ready'; toolUseId: string; input: unknown }
  | { type: 'tool-complete'; toolUseId: string; status: 'success' | 'error'; output: string };

interface SsoLoginResponse {
  is_authenticated?: boolean;
  authn_endpoint?: string;
}

interface AgentEvent {
  type?: string;
  content?: unknown;
  tool_id?: unknown;
  tool_name?: unknown;
  input?: unknown;
  result?: unknown;
  event?: {
    contentBlockStart?: {
      start?: {
        toolUse?: {
          toolUseId?: unknown;
          name?: unknown;
        };
      };
    };
    contentBlockDelta?: {
      delta?: {
        text?: unknown;
        reasoningContent?: {
          text?: unknown;
        };
        toolUse?: {
          input?: unknown;
        };
      };
    };
    contentBlockStop?: unknown;
  };
  message?: {
    content?: unknown;
  };
}

export class AgentRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'AgentRequestError';
  }
}

function parseJsonValue(value: string): unknown {
  let parsedValue: unknown = JSON.parse(value);

  for (let encodingLevel = 0; encodingLevel < 2 && typeof parsedValue === 'string'; encodingLevel += 1) {
    const nestedValue = parsedValue.startsWith('data:') ? parsedValue.slice(5).trim() : parsedValue;
    parsedValue = JSON.parse(nestedValue);
  }

  return parsedValue;
}

function stringifyToolResultContent(content: unknown): string {
  if (typeof content === 'string') {
    return content;
  }

  if (!Array.isArray(content)) {
    return content == null ? '' : JSON.stringify(content, null, 2);
  }

  return content
    .map((block) => {
      if (block && typeof block === 'object') {
        const textBlock = (block as { text?: unknown }).text;
        if (typeof textBlock === 'string') {
          return textBlock;
        }
        const jsonBlock = (block as { json?: unknown }).json;
        if (jsonBlock !== undefined) {
          return JSON.stringify(jsonBlock, null, 2);
        }
      }
      return typeof block === 'string' ? block : JSON.stringify(block);
    })
    .join('\n');
}

function parseMessageToolEvents(content: unknown): AgentStreamEvent[] {
  if (!Array.isArray(content)) {
    return [];
  }

  return content.flatMap((block): AgentStreamEvent[] => {
    if (!block || typeof block !== 'object') {
      return [];
    }

    const toolUse = (block as { toolUse?: unknown }).toolUse;
    if (toolUse && typeof toolUse === 'object') {
      const { toolUseId, input } = toolUse as { toolUseId?: unknown; input?: unknown };
      return typeof toolUseId === 'string' ? [{ type: 'tool-ready', toolUseId, input }] : [];
    }

    const toolResult = (block as { toolResult?: unknown }).toolResult;
    if (toolResult && typeof toolResult === 'object') {
      const {
        toolUseId,
        status,
        content: resultContent,
      } = toolResult as {
        toolUseId?: unknown;
        status?: unknown;
        content?: unknown;
      };
      return typeof toolUseId === 'string'
        ? [
            {
              type: 'tool-complete',
              toolUseId,
              status: status === 'error' ? 'error' : 'success',
              output: stringifyToolResultContent(resultContent),
            },
          ]
        : [];
    }

    return [];
  });
}

export function parseAgentStreamLine(line: string): AgentStreamEvent[] {
  const trimmedLine = line.trim();

  if (!trimmedLine || trimmedLine.startsWith(':') || trimmedLine.startsWith('event:')) {
    return [];
  }

  const payload = trimmedLine.startsWith('data:') ? trimmedLine.slice(5).trim() : trimmedLine;
  if (!payload || payload === '[DONE]') {
    return [];
  }

  let parsedEvent: unknown;
  try {
    parsedEvent = parseJsonValue(payload);
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    console.warn(`Skipping malformed agent stream line (${reason}): ${payload.slice(0, 200)}`);
    return [];
  }

  if (!parsedEvent || typeof parsedEvent !== 'object') {
    return [];
  }

  if ('data' in parsedEvent && 'delta' in parsedEvent) {
    // Strands emits a wrapper event that duplicates the native Bedrock delta;
    // the agent's own formatters skip it, so we do the same to avoid double text.
    return [];
  }

  const agentEvent = parsedEvent as AgentEvent;
  if (agentEvent.type === 'text' && typeof agentEvent.content === 'string') {
    return [{ type: 'text', content: agentEvent.content }];
  }

  if (agentEvent.type === 'reasoning' && typeof agentEvent.content === 'string') {
    return [{ type: 'thinking', content: agentEvent.content }];
  }

  if (agentEvent.type === 'error') {
    const message =
      typeof agentEvent.content === 'string'
        ? agentEvent.content
        : agentEvent.content == null
          ? 'The agent reported an unspecified error.'
          : JSON.stringify(agentEvent.content);
    return [{ type: 'error', message }];
  }

  if (agentEvent.type === 'tool_execution' && typeof agentEvent.tool_name === 'string') {
    const toolUseId = typeof agentEvent.tool_id === 'string' ? agentEvent.tool_id : agentEvent.tool_name;
    const input =
      typeof agentEvent.input === 'string' ? agentEvent.input : (JSON.stringify(agentEvent.input ?? {}) ?? '');
    return [
      { type: 'tool-start', toolUseId, name: agentEvent.tool_name },
      { type: 'tool-ready', toolUseId, input },
      ...(agentEvent.result === undefined
        ? []
        : [
            {
              type: 'tool-complete' as const,
              toolUseId,
              status: 'success' as const,
              output: stringifyToolResultContent(agentEvent.result),
            },
          ]),
    ];
  }

  const toolStart = agentEvent.event?.contentBlockStart?.start?.toolUse;
  if (toolStart && typeof toolStart.toolUseId === 'string' && typeof toolStart.name === 'string') {
    return [{ type: 'tool-start', toolUseId: toolStart.toolUseId, name: toolStart.name }];
  }

  const bedrockDelta = agentEvent.event?.contentBlockDelta?.delta;
  if (typeof bedrockDelta?.text === 'string') {
    return [{ type: 'text', content: bedrockDelta.text }];
  }

  if (typeof bedrockDelta?.reasoningContent?.text === 'string') {
    return [{ type: 'thinking', content: bedrockDelta.reasoningContent.text }];
  }

  if (typeof bedrockDelta?.toolUse?.input === 'string') {
    return [{ type: 'tool-input', content: bedrockDelta.toolUse.input }];
  }

  if (agentEvent.event?.contentBlockStop) {
    return [{ type: 'tool-stop' }];
  }

  return parseMessageToolEvents(agentEvent.message?.content);
}

function assembleSseFrameData(frame: string): string {
  const dataFieldLines = frame.split(/\r?\n/).filter((frameLine) => frameLine.startsWith('data:'));

  if (dataFieldLines.length === 0) {
    return frame.trim();
  }

  return dataFieldLines.map((dataFieldLine) => dataFieldLine.slice(5).replace(/^ /, '')).join('\n');
}

export function consumeAgentStreamChunk(
  bufferedFrame: string,
  chunk: string,
): { bufferedFrame: string; events: AgentStreamEvent[] } {
  const frames = `${bufferedFrame}${chunk}`.split(/\r?\n\r?\n/);
  const nextBufferedFrame = frames.pop() ?? '';
  const events = frames.flatMap((frame) => parseAgentStreamLine(assembleSseFrameData(frame)));

  return { bufferedFrame: nextBufferedFrame, events };
}

export function createAgentRequest(prompt: string, sessionId: string, signal: AbortSignal): RequestInit {
  return {
    method: 'POST',
    credentials: 'include',
    signal,
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
    },
    body: JSON.stringify({
      prompt,
      sessionId,
      stream_format: 'bedrock',
    }),
  };
}

async function readSsoLoginResponse(response: Response, step: string): Promise<SsoLoginResponse> {
  if (!response.ok) {
    throw new Error(`Discovery Agent authentication failed during ${step} with HTTP ${response.status}.`);
  }

  try {
    return (await response.json()) as SsoLoginResponse;
  } catch {
    throw new Error(`Discovery Agent authentication returned an invalid response during ${step}.`);
  }
}

export async function ensureAgentAuthentication(endpoint: string): Promise<void> {
  const loginUrl = new URL('/sso/login', endpoint);
  const loginStatus = await readSsoLoginResponse(await fetch(loginUrl, { credentials: 'include' }), 'session check');

  if (loginStatus.is_authenticated) {
    return;
  }

  if (!loginStatus.authn_endpoint) {
    throw new Error('Discovery Agent authentication did not provide a Midway login endpoint.');
  }

  const tokenResponse = await fetch(loginStatus.authn_endpoint, { credentials: 'include' });
  if (!tokenResponse.ok) {
    throw new Error(`Discovery Agent Midway token request failed with HTTP ${tokenResponse.status}.`);
  }

  const idToken = (await tokenResponse.text()).trim();
  if (!idToken) {
    throw new Error('Discovery Agent Midway token request returned an empty token.');
  }

  loginUrl.searchParams.set('id_token', idToken);

  const exchangeStatus = await readSsoLoginResponse(
    await fetch(loginUrl, { credentials: 'include' }),
    'token exchange',
  );

  if (!exchangeStatus.is_authenticated) {
    throw new Error('Discovery Agent authentication failed.');
  }
}

async function getResponseError(response: Response): Promise<string> {
  const responseBody = await response.text();
  const responseDetail = responseBody.trim() ? ` ${responseBody.trim()}` : '';

  if (response.status === 401) {
    return `The Midway session expired.${responseDetail}`;
  }

  if (response.status === 403) {
    return `The current user does not have Execute permission for this IRIS agent.${responseDetail}`;
  }

  return `The IRIS agent request failed with HTTP ${response.status}.${responseDetail}`;
}

export async function streamAgentResponse({
  endpoint,
  prompt,
  sessionId,
  signal,
  onEvent,
}: AgentStreamRequest): Promise<void> {
  await ensureAgentAuthentication(endpoint);
  const response = await fetch(endpoint, createAgentRequest(prompt, sessionId, signal));

  if (!response.ok) {
    throw new AgentRequestError(await getResponseError(response), response.status);
  }

  if (!response.body) {
    throw new Error('The IRIS agent response did not include a readable stream.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let bufferedFrame = '';
  let streamRead = await reader.read();

  while (!streamRead.done) {
    const parsedChunk = consumeAgentStreamChunk(bufferedFrame, decoder.decode(streamRead.value, { stream: true }));
    bufferedFrame = parsedChunk.bufferedFrame;
    parsedChunk.events.forEach(onEvent);
    streamRead = await reader.read();
  }

  const finalChunk = decoder.decode();
  const parsedFinalChunk = consumeAgentStreamChunk(bufferedFrame, `${finalChunk}\n\n`);
  parsedFinalChunk.events.forEach(onEvent);
}
